import { type FC, type ReactNode, useMemo } from "react";

import { Card } from "@mantine/core";
import { Colors, Tooltip } from "@snapchat/snap-design-system";
import { InformationIcon } from "@snapchat/snap-design-system-icons";
import { addDays, differenceInDays, differenceInHours, isBefore } from "date-fns";

import { SkeletonContainer } from "../../../camkit/common/components";
import { MetricField } from "../../../gql-generated/types";
import { GrayLabel } from "../../../hosted-lenses/components/HostedLensSection.styles";
import { makeUTCTimestampWithStart } from "../../../lens-insights/converters";
import { getStatsTotal } from "../../../lens-insights/filters";
import { useOrganicStatsQuery } from "../../../lens-insights/hooks";
import { Flexbox } from "../../../shared/components";
import { useCreatorRewardRecord } from "../../hooks";
import { makeDisplayMeta } from "../../state";
import {
    CREATOR_REWARDS_DAYS_THRESHOLD,
    CREATOR_REWARDS_DOCS_LINK,
    CREATOR_REWARDS_FAQ_LINK,
    CREATOR_REWARDS_POSTERS_THRESHOLD,
} from "../constants";

import {
    DaysLeftText,
    DaysNumberTag,
    PostersText,
    headingCss,
    subSectionCss,
} from "./LensCreatorRewardsInsights.styles";
import {
    Day,
    Days,
    Docs,
    EligibilityPeriod,
    EligibilityPeriodTooltip,
    Hour,
    Hours,
    NumDaysEligible,
    QualifiedRegions,
    TimeSuffix,
    TotalQualifiedPosters,
    TotalQualifiedPostersTooltip,
} from "./messages.generated";

interface LensCreatorRewardsInsightsProps {
    creationDate: Date;
    lensId: string;
}

export const LensCreatorRewardsInsights: FC<LensCreatorRewardsInsightsProps> = ({ creationDate, lensId }) => {
    /*
    because we truncate the start date to 12AM UTC, we need to add 1 day to the end date to include the last day


        |                         |                  |                       |
        -------------|------------------------------------------|-------------
        |        lens created     |                  |     lens created+90   |
   1st:UTC12AM     UTC5AM    2nd:UTC12AM       90st:UTC12AM            91st:UTC12AM

     */

    /** UTC Timestamps for Organic Stats */
    const startEndTimestamp = makeUTCTimestampWithStart(CREATOR_REWARDS_DAYS_THRESHOLD + 1, creationDate);

    /** Date with TimeOffeset (local time zone) for LCR  */
    const now = new Date();
    const eligibilityEndDate = addDays(creationDate, CREATOR_REWARDS_DAYS_THRESHOLD);
    const daysLeft = differenceInDays(eligibilityEndDate, now);
    const hoursLeft = daysLeft <= 0 ? differenceInHours(eligibilityEndDate, now) : 0;
    const withinDaysThreshold = isBefore(now, eligibilityEndDate);

    const { loading: lensPosterUniquesS11Loading, data: lensPosterUniquesS11 } = useOrganicStatsQuery({
        isAllTimeSelected: false,
        isTotal: true,
        metricFields: [MetricField.LensPosterUniquesS11],
        startEndTimestamp,
        skip: !withinDaysThreshold,
    });

    const { loading: creatorRewardRecordLoading, postUU, rewardEligibility } = useCreatorRewardRecord({ lensId });

    const isDataLoading = lensPosterUniquesS11Loading || creatorRewardRecordLoading;

    const postersUniqueTotal = getStatsTotal(lensPosterUniquesS11);
    const postersLeft = CREATOR_REWARDS_POSTERS_THRESHOLD - postersUniqueTotal;

    const { postersText, postersHeading, postersTextColor } = useMemo(
        () =>
            makeDisplayMeta({
                withinDaysThreshold,
                rewardEligibility,
                postersUniqueTotal,
                postersLeft,
                postUU,
            }),
        [postUU, postersLeft, postersUniqueTotal, rewardEligibility, withinDaysThreshold],
    );

    const timeLeftText = (): ReactNode => {
        if (daysLeft > 0) {
            return (
                <>
                    <DaysNumberTag withinDaysThreshold={withinDaysThreshold}>{daysLeft}</DaysNumberTag>{" "}
                    {daysLeft === 1 ? <Day /> : <Days />} <TimeSuffix />
                </>
            );
        }

        return (
            <>
                <DaysNumberTag withinDaysThreshold={withinDaysThreshold}>{Math.max(hoursLeft, 0)}</DaysNumberTag>{" "}
                {hoursLeft === 1 ? <Hour /> : <Hours />} <TimeSuffix />
            </>
        );
    };

    return (
        <Card shadow="sm" radius="md">
            {isDataLoading ? (
                <SkeletonContainer loading />
            ) : (
                <Flexbox>
                    <div className={subSectionCss}>
                        <GrayLabel>
                            <EligibilityPeriod />
                            <Tooltip content={<EligibilityPeriodTooltip />}>
                                <InformationIcon fill={Colors.Gray.V250} />
                            </Tooltip>
                        </GrayLabel>
                        <div className={headingCss}>
                            <NumDaysEligible />
                        </div>
                        <DaysLeftText withinDaysThreshold={withinDaysThreshold}>{timeLeftText()}</DaysLeftText>
                    </div>
                    <div className={subSectionCss}>
                        <GrayLabel>
                            <TotalQualifiedPosters />
                            <Tooltip
                                content={
                                    <TotalQualifiedPostersTooltip
                                        qualifiedRegionsLink={
                                            <a href={CREATOR_REWARDS_DOCS_LINK} target="_blank" rel="noreferrer">
                                                <QualifiedRegions />
                                            </a>
                                        }
                                        docsLink={
                                            <a href={CREATOR_REWARDS_FAQ_LINK} target="_blank" rel="noreferrer">
                                                <Docs />
                                            </a>
                                        }
                                    />
                                }
                            >
                                <InformationIcon fill={Colors.Gray.V250} />
                            </Tooltip>
                        </GrayLabel>
                        <div className={headingCss}>{postersHeading}</div>
                        <PostersText color={postersTextColor}> {postersText}</PostersText>
                    </div>
                </Flexbox>
            )}
        </Card>
    );
};
